<template>
  <div>
    <MButton v-bind="getBindValue" @click="clickHandler">
      <slot>{{ t('common.import') }}</slot>
    </MButton>

    <ImportModalV2
      v-if="V2"
      v-bind="pick(props, IMPORT_PROP_KEYS)"
      :template="{ ...props.template, templateKey, modelId }"
      @register="registerModalV2"
      @ok="importDone"
    >
      <template v-for="item in Object.keys($slots)" #[item]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
    </ImportModalV2>

    <ImportModal
      v-else
      v-bind="pick(props, IMPORT_PROP_KEYS)"
      @register="registerModal"
      @ok="importDone"
    >
      <template v-for="item in Object.keys($slots)" #[item]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
    </ImportModal>
  </div>
</template>

<script setup lang="ts">
  import { Modal } from 'ant-design-vue';
  import { ComponentOptionsMixin, computed, unref } from 'vue';
  import { useAttrs } from '@mts-fe/hooks';
  import { omit, pick } from '@mts-fe/utils';
  import { MButton, MButtonProps } from '@mts/components/Button';
  import { ModalProps, useModal } from '@mts/components/Modal';
  import { t } from '@mts/hooks/web/useI18n';
  import { warn } from '@mts/utils';
  import type { ImportProps } from '..';
  import ImportModal from './ImportModal.vue';
  import ImportModalV2 from './ImportModalV2.vue';

  /** 弹窗导入-按钮 */
  defineOptions({
    name: 'ModalImportBtn',
    extends: MButton as ComponentOptionsMixin,
    inheritAttrs: false,
  });

  export type ModalImportProps = MButtonProps &
    ImportProps & {
      /** 弹窗配置 */
      modal?: ModalProps;
      popconfirm?: string;
    };

  const IMPORT_PROP_KEYS = ['action', 'params', 'templateKey', 'modelId'];
  const props = withDefaults(defineProps<ModalImportProps>(), {
    template: () => ({}),
    V2: false,
  });
  const attrs = useAttrs();
  const getBindValue = computed(() => ({
    ...unref(attrs),
    ...omit(props, [...IMPORT_PROP_KEYS, 'modal', 'template', 'V2']),
  }));

  const [registerModal, { openModal, setModalProps }] = useModal();
  const [registerModalV2, modalAction] = useModal();
  const openImport = () => {
    if (props.V2) {
      modalAction.setModalProps({ ...props.modal });
      modalAction.openModal(true);
      return;
    }

    warn('弹窗导入新版可用，请升级到新版', { V2: true });
    setModalProps({ ...props.modal });
    openModal(true);
  };

  const clickHandler = () => {
    if (props.popconfirm) {
      Modal.confirm({
        content: t(props.popconfirm),
        icon: null,
        onOk: () => openImport(),
      });
    } else {
      openImport();
    }
  };

  const emit = defineEmits(['ok']);
  const importDone = (...args) => emit('ok', ...args);
</script>
